/**
 * Camera placement maths for the two external views, kept pure so the follow
 * behaviour can be tested without a renderer.
 *
 * Both cameras work off the aircraft's *flight direction* rather than its full
 * attitude: a chase camera rigidly bolted to the airframe rolls with it, which
 * turns a barrel roll into a spinning horizon and loses the ground reference
 * that makes low flying readable. Trailing the nose direction and keeping the
 * camera's own up vector on world up is the era-appropriate "external view"
 * the original's VCR offers, and it degrades gracefully when the aircraft is
 * inverted or vertical.
 */

import {
  addVec3,
  BODY_FORWARD,
  clamp,
  lengthVec3,
  normalizeVec3,
  rotateVec3,
  scaleVec3,
  type Quat,
  type Vec3,type Pose
} from "@red-opus/engine";

export const WORLD_UP: Vec3 = { x: 0, y: 1, z: 0 };

export interface ChaseCameraSpec {
  /** Metres behind the aircraft, along its nose direction. */
  readonly trailM: number;
  /** Metres above it, along world up. */
  readonly heightM: number;
  /** Metres ahead of the aircraft the camera aims, so the nose sits low in frame. */
  readonly leadM: number;
  /** Exponential-smoothing rate, per second. Higher snaps harder to the goal. */
  readonly followRate: number;
  /** The camera never drops below this height above the ground plane. */
  readonly minAltitudeM: number;
}

/**
 * `trailM` is the *goal* distance; smoothing means the camera settles roughly
 * `speed / followRate` metres further back than that while accelerating away
 * from it (about 7 m at cruise), which is what keeps a chase view from feeling
 * bolted on. Both numbers are picked together so the airframe fills a useful
 * part of the frame at cruise without clipping the near plane in a hard pull.
 */
export const CHASE_CAMERA: ChaseCameraSpec = {
  trailM: 16,
  heightM: 4,
  leadM: 22,
  followRate: 6,
  minAltitudeM: 2,
};

/** World-space nose direction of a pose. */
export function forwardOf(orientation: Quat): Vec3 {
  return rotateVec3(orientation, BODY_FORWARD);
}

/** Nose direction projected onto the ground plane; falls back to north when the nose is vertical. */
export function groundHeadingOf(orientation: Quat): Vec3 {
  const forward = forwardOf(orientation);
  return normalizeVec3({ x: forward.x, y: 0, z: forward.z }, { x: 0, y: 0, z: -1 });
}

function liftAboveGround(point: Vec3, minAltitudeM: number): Vec3 {
  return point.y >= minAltitudeM ? point : { x: point.x, y: minAltitudeM, z: point.z };
}

/** Where the chase camera wants to be for this pose, before smoothing. */
export function chaseCameraGoal(pose: Pose, spec: ChaseCameraSpec = CHASE_CAMERA): Vec3 {
  const forward = forwardOf(pose.orientation);
  const behind = scaleVec3(forward, -spec.trailM);
  const above = scaleVec3(WORLD_UP, spec.heightM);
  return liftAboveGround(addVec3(pose.position, addVec3(behind, above)), spec.minAltitudeM);
}

/** What the chase camera looks at: slightly ahead of the aircraft. */
export function chaseCameraTarget(pose: Pose, spec: ChaseCameraSpec = CHASE_CAMERA): Vec3 {
  return addVec3(pose.position, scaleVec3(forwardOf(pose.orientation), spec.leadM));
}

/**
 * Frame-rate-independent exponential smoothing: the fraction of the remaining
 * distance covered depends on elapsed time, not on how many frames elapsed, so
 * the camera behaves the same at 30 and 144 Hz.
 */
export function smoothFollow(current: Vec3, goal: Vec3, rate: number, dt: number): Vec3 {
  if (!(dt > 0) || rate <= 0) return current;
  const blend = 1 - Math.exp(-rate * dt);
  return addVec3(current, scaleVec3({ x: goal.x - current.x, y: goal.y - current.y, z: goal.z - current.z }, blend));
}

/**
 * AD-015's kill camera: a boom trailing the victim while it falls.
 *
 * Not `ChaseCameraSpec` with different numbers, because the *direction* the
 * boom trails in is decided differently. A chase camera trails the live nose
 * direction, which is right for an aeroplane somebody is flying; a wreck
 * weathervanes nose-first into its own airflow (AD-014 §3c) and ends the fall
 * pointing straight down, so trailing its nose would put the camera directly
 * overhead looking at the ground with no horizon in frame — the one thing that
 * would make a 26-second plunge unreadable. The boom's bearing is instead
 * frozen at the fatal blow, while the aeroplane is still roughly level, and the
 * camera then simply follows the wreck down on a fixed offset. The horizon
 * stays level and the ground comes up to meet it.
 */
export interface KillCamSpec {
  /** Metres behind the victim, along the frozen bearing. */
  readonly trailM: number;
  readonly heightM: number;
  /** Slower than the chase camera's: the lag is what makes the wreck fall *away* from the lens. */
  readonly followRate: number;
  readonly minAltitudeM: number;
}

/** CALIBRATION-PENDING — framing is the half of this only the user's own eye can settle. */
export const KILL_CAM_CAMERA: KillCamSpec = {
  trailM: 46,
  heightM: 14,
  followRate: 2.2,
  minAltitudeM: 12,
};

/**
 * Where the kill camera wants to be, before smoothing. `bearing` is the
 * victim's ground heading frozen when the window opened — see `KillCamSpec`.
 */
export function killCamGoal(pose: Pose, bearing: Vec3, spec: KillCamSpec = KILL_CAM_CAMERA): Vec3 {
  const behind = scaleVec3(bearing, -spec.trailM);
  const above = scaleVec3(WORLD_UP, spec.heightM);
  return liftAboveGround(addVec3(pose.position, addVec3(behind, above)), spec.minAltitudeM);
}

export interface FlyByCameraSpec {
  /** Metres ahead of the aircraft the camera plants itself. */
  readonly aheadM: number;
  /** Metres to the right of its track. */
  readonly offsetM: number;
  readonly heightM: number;
  readonly minAltitudeM: number;
  /** Re-plant once the aircraft is further away than this. */
  readonly maxRangeM: number;
}

export const FLY_BY_CAMERA: FlyByCameraSpec = {
  aheadM: 200,
  offsetM: 55,
  heightM: 14,
  minAltitudeM: 12,
  maxRangeM: 600,
};

/**
 * Plants a stationary camera ahead of and beside the aircraft's ground track,
 * so it flies past the lens rather than being followed — the classic external
 * "fly-by" shot.
 */
export function flyByAnchor(pose: Pose, spec: FlyByCameraSpec = FLY_BY_CAMERA): Vec3 {
  const heading = groundHeadingOf(pose.orientation);
  const right: Vec3 = { x: -heading.z, y: 0, z: heading.x };
  const placed = addVec3(
    pose.position,
    addVec3(scaleVec3(heading, spec.aheadM), addVec3(scaleVec3(right, spec.offsetM), scaleVec3(WORLD_UP, spec.heightM))),
  );
  return liftAboveGround(placed, spec.minAltitudeM);
}

/** True once the aircraft has flown far enough past the anchor that a new one is due. */
export function needsNewFlyByAnchor(anchor: Vec3, position: Vec3, spec: FlyByCameraSpec = FLY_BY_CAMERA): boolean {
  return lengthVec3({ x: position.x - anchor.x, y: position.y - anchor.y, z: position.z - anchor.z }) > spec.maxRangeM;
}

/**
 * Mouse-wheel zoom for the fly-by camera (user request, 2026-08-20): a factor
 * on `FlyByCameraSpec`'s placement distances, not the lens's field of view —
 * "zoom in" pulls the whole stationary vantage point toward the aircraft's
 * track rather than cropping the shot, so the camera stays the fixed-point
 * "plane flies past the lens" style the fly-by is built on. 0.3x-3x keeps
 * both ends useful: below 0.3x the anchor would sit closer than is readable
 * on a fast pass, above 3x the aircraft is a speck.
 */
export interface FlyByZoomSpec {
  readonly min: number;
  readonly max: number;
  readonly default: number;
}

export const FLY_BY_ZOOM: FlyByZoomSpec = {
  min: 0.3,
  max: 3,
  default: 1,
};

export function clampFlyByZoom(factor: number): number {
  return clamp(factor, FLY_BY_ZOOM.min, FLY_BY_ZOOM.max);
}

/**
 * Wheel travel per notch that moves the zoom goal a full unit of factor.
 * `PointerFrame.throttleDelta` reports ~0.08 for one notch (`pointer.ts`);
 * `2.5` turns that into a ~0.2x step, so crossing the whole 0.3x-3x range is a
 * handful of deliberate notches rather than a hair-trigger or a chore.
 */
const FLY_BY_ZOOM_PER_WHEEL_UNIT = 2.5;

/**
 * One frame's wheel travel folded into a zoom goal. `wheelDelta` is the same
 * signed unit `throttleDelta` already is (positive is wheel-up, the "scroll
 * towards the top of the document" the browser reports for a notch pushed
 * away from the user) — and wheel-up is throttle-*up* on the lever mapping,
 * so here, to keep one physical gesture meaning one thing, wheel-up also
 * *closes the distance*: the map/photo-viewer "scroll up to get closer"
 * convention, and the more natural pairing with "push forward to zoom in".
 */
export function flyByZoomGoalAfterWheel(goal: number, wheelDelta: number): number {
  return clampFlyByZoom(goal - wheelDelta * FLY_BY_ZOOM_PER_WHEEL_UNIT);
}

/** Exponential-smoothing rate for the zoom factor chasing its goal, per second — quick enough to read as a live dolly, slow enough that a fast flick doesn't hard-cut the shot. */
export const FLY_BY_ZOOM_SMOOTH_RATE = 8;

/** `smoothFollow`'s maths without the `Vec3` wrapper, for a lone scalar like the fly-by zoom factor. */
export function smoothScalar(current: number, goal: number, rate: number, dt: number): number {
  if (!(dt > 0) || rate <= 0) return current;
  return current + (goal - current) * (1 - Math.exp(-rate * dt));
}

/**
 * `spec` scaled by the zoom factor. `minAltitudeM` is an absolute floor above
 * terrain, not a placement distance, so it stays fixed; `maxRangeM` scales
 * with the rest so a zoomed-out anchor — already most of the way to the
 * *unscaled* replant range the moment it's planted — doesn't fire
 * `needsNewFlyByAnchor` again almost every frame.
 */
export function zoomedFlyByCameraSpec(zoom: number, spec: FlyByCameraSpec = FLY_BY_CAMERA): FlyByCameraSpec {
  return {
    ...spec,
    aheadM: spec.aheadM * zoom,
    offsetM: spec.offsetM * zoom,
    heightM: spec.heightM * zoom,
    maxRangeM: spec.maxRangeM * zoom,
  };
}
